<template>
  <div class="tabs is-boxed mb-3">
    <ul>
      <li v-for="status in statuses" :key="status" :class="{ 'is-active': selected === status }">
        <a
          v-if="status === 'Cases' && !canViewCases"
          class="has-text-grey-light"
          style="cursor: not-allowed"
          :title="`Hiervoor heb je recht ${CASES_PERMISSION} nodig`"
        >
          Cases
        </a>
        <a v-else @click="emit('select', status)">
          {{ LABELS[status] || status }}
          <span
            v-if="taskStatus(status)"
            class="status-dot ml-1"
            :class="statusColor(taskStatus(status))"
            :title="taskTitle(status)"
          ></span>
          <span v-if="status === 'Tickets' && tickets" class="tag is-rounded is-light ml-1">
            {{ tickets.length }}
          </span>
          <span
            v-if="status === 'Cases' && cases"
            class="tag is-rounded ml-1"
            :class="openCases ? 'is-warning' : 'is-light'"
            :title="`${cases.length} cases, ${openCases} open`"
          >
            {{ cases.length }}
          </span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup>
// The detail tabs of the call centre address. The status values stay as the manager knows them
// (they also build the tasks link), only the labels differ.
import { computed } from 'vue'
import { useAddressTickets } from './composables/useAddressTickets'
import { useAddressCases, CASES_PERMISSION } from './composables/useAddressCases'
import { hasPermission } from '@/logic/common/Permissions'
import { formatRelativeNl } from '@/util/DateTime'

const LABELS = { Client: 'Bewoner', Eleena: 'Monitoring', Inspection: 'Schouw', Installation: 'Installatie' }

const props = defineProps({
  statuses: { type: Array, required: true },
  selected: { type: String, required: true },
  address: { type: String, default: '' },
  // client.tasks: { design: { status }, sales: {...}, ... }
  tasks: { type: Object, default: () => ({}) },
  // tickets location id of the address, for its cases
  locationId: { type: String, default: null },
})
const emit = defineEmits(['select'])

// the task tabs carry the status of their task
const taskStatus = (status) => props.tasks?.[status.toLowerCase()]?.status || null

const taskTitle = (status) => {
  const age = formatRelativeNl(props.tasks?.[status.toLowerCase()]?.status_at)
  return `Status ${(LABELS[status] || status).toLowerCase()}: ${taskStatus(status)}${age ? ` (${age})` : ''}`
}

// statuses come from the tasks-service state machine, so colour by keyword
const statusColor = (status) => {
  const s = String(status).toLowerCase()
  if (/approved|goedgekeurd|finished|afgerond|done|completed/.test(s)) return 'is-green'
  if (/rejected|afgekeurd|cancel|geannuleerd/.test(s)) return 'is-red'
  if (/created|new|nieuw|te plannen/.test(s)) return 'is-grey'
  return 'is-orange'
}

const canViewCases = hasPermission([CASES_PERMISSION])
const { data: cases } = useAddressCases(computed(() => props.locationId))
// the badge is orange while a case still needs attention
const openCases = computed(
  () => (cases.value ?? []).filter((c) => ['created', 'open'].includes(c.status)).length
)

const { data: tickets } = useAddressTickets(computed(() => props.address))
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  vertical-align: middle;
}
.status-dot.is-green { background-color: #48c78e; }
.status-dot.is-red { background-color: #f14668; }
.status-dot.is-grey { background-color: #b5b5b5; }
.status-dot.is-orange { background-color: #ff9f43; }
</style>
